import type { QueryClient } from '@tanstack/react-query'
import { getDocuments } from '../../api/document/document'
import { fetchAllEmployeeEvaluations } from '../../api/evaluations/evaluations'
import { fetchInterviewList, fetchCandidate, fetchCandidateList, fetchVacancy, fetchVacancyList } from '../../api/recruitment/recruitment'
import { fetchContent, fetchContentList } from '../../api/content/content'
import { getProjectById, getProjectDetailSnapshot, getProjectSummary, getTimeEntries } from '../../api/time_tracking/requests'
import { fetchEmployee, fetchEmployeeList, fetchTeam, fetchTeamList } from '../../api/workforce/workforce'

const routePreloaders: Array<{ matches: (path: string) => boolean; load: () => Promise<unknown> }> = [
  { matches: (path) => path.startsWith('/user/home'), load: () => import('../../pages/user/HomePage/HomePage') },
  { matches: (path) => path.startsWith('/user/workforce'), load: () => import('../../pages/user/Workforce/UserWorkforce') },
  { matches: (path) => path.startsWith('/user/planning'), load: () => import('../../pages/user/Planning/PlanningPage') },
  { matches: (path) => path.startsWith('/user/payroll'), load: () => import('../../pages/user/Payroll/PayrollPage') },
  { matches: (path) => path.startsWith('/user/recruitment'), load: () => import('../../pages/user/Recruitment/RecruitmentPage') },
  { matches: (path) => path.startsWith('/user/admin/document') || path.startsWith('/user/document'), load: () => import('../../pages/user/Document/DocumentPage') },
  { matches: (path) => path.startsWith('/user/content'), load: () => import('../../pages/user/Content/ContentPage') },
  { matches: (path) => path.startsWith('/user/explore'), load: () => import('../../pages/user/Explore/ExplorePage') },
  { matches: (path) => path.startsWith('/user/evaluations'), load: () => import('../../pages/user/Evaluations/EvaluationsPage') },
  { matches: (path) => path.startsWith('/user/analytics'), load: () => import('../../pages/user/Analytics/AnalyticsPage') },
  { matches: (path) => path.startsWith('/user/settings'), load: () => import('../../pages/user/Settings/SettingsPage') },
  { matches: (path) => path.startsWith('/user/classification'), load: () => import('../../pages/user/Classification/ClassificationPage') },
  { matches: (path) => path.startsWith('/user/outflow'), load: () => import('../../pages/user/Outflow/OutflowPage') },
  { matches: (path) => path.startsWith('/user/profile'), load: () => import('../../pages/user/Profile/ProfilePage') },
  { matches: (path) => path.startsWith('/user/faq'), load: () => import('../../pages/user/FAQ/FAQPage') },
]

const warmedPaths = new Set<string>()
const warmedPathData = new Set<string>()
const warmedRows = new Set<string>()

const tabDataPrefetchers: Array<{
  matches: (path: string) => boolean
  prefetch: (queryClient: QueryClient, path: string) => Promise<unknown>
}> = [
  {
    matches: (path) => path === '/user/workforce/people',
    prefetch: (queryClient) =>
      queryClient.prefetchQuery({
        queryKey: ['people', 1, '', '{}', 'recent'],
        staleTime: 60 * 1000,
        queryFn: () =>
          fetchEmployeeList({
            search: '',
            page: 1,
            pageSize: 15,
            filters: {},
            ordering: '-created_at',
            expand: 'teams.id,teams.name,managed_teams,current_position',
            fields:
              'id,first_name,last_name,full_name,profile_picture,current_position.id,current_position.title,teams.id,teams.name,managed_teams.id,managed_teams.name,country_of_work,work_email',
          }),
      }),
  },
  {
    matches: (path) => path === '/user/workforce/teams',
    prefetch: (queryClient) =>
      queryClient.prefetchQuery({
        queryKey: ['teams', 1, '', '{}', false],
        staleTime: 60 * 1000,
        queryFn: () =>
          fetchTeamList({
            search: '',
            page: 1,
            pageSize: 15,
            filters: {},
            expand: 'team_manager',
            fields:
              'id,name,country,members,team_manager.id,team_manager.first_name,team_manager.last_name,team_manager.profile_picture,team_manager.work_email',
          }),
      }),
  },
  {
    matches: (path) => path === '/user/content',
    prefetch: (queryClient) =>
      queryClient.prefetchQuery({
        // Must match ContentTable's useTableState key: [...baseKey, page, search, filters, sortBy, extraParams].
        // baseKey=['content','table','editable'], itemsPerPage=10; React Query hashes {assigned_to: undefined} -> {}.
        queryKey: ['content', 'table', 'editable', 1, '', {}, '', {}],
        staleTime: 60 * 1000,
        queryFn: () =>
          fetchContentList({
            page: 1,
            pageSize: 10,
            search: '',
            filters: {},
          }),
      }),
  },
  {
    matches: (path) => path === '/user/recruitment/candidates',
    prefetch: (queryClient) =>
      queryClient.prefetchQuery({
        queryKey: ['candidates', 1, '', '{}'],
        staleTime: 60 * 1000,
        queryFn: () =>
          fetchCandidateList({
            search: '',
            page: 1,
            pageSize: 15,
            filters: {},
          }),
      }),
  },
  {
    matches: (path) => path === '/user/recruitment/interviews',
    prefetch: (queryClient) =>
      queryClient.prefetchQuery({
        queryKey: ['interviews', 1, '', '{}'],
        staleTime: 60 * 1000,
        queryFn: () =>
          fetchInterviewList({
            search: '',
            page: 1,
            pageSize: 15,
            filters: {},
          }),
      }),
  },
  {
    matches: (path) => path === '/user/recruitment/vacancy',
    prefetch: (queryClient) =>
      queryClient.prefetchQuery({
        queryKey: ['vacancy-texts', 1, '', '{}'],
        staleTime: 60 * 1000,
        queryFn: () =>
          fetchVacancyList({
            search: '',
            page: 1,
            pageSize: 15,
            filters: {},
          }),
      }),
  },
  {
    matches: (path) => path === '/user/evaluations',
    prefetch: (queryClient) =>
      queryClient.prefetchQuery({
        queryKey: ['evaluation-summary', 1, '', '{"active":["true"]}', 'recent'],
        staleTime: 60 * 1000,
        queryFn: () =>
          fetchAllEmployeeEvaluations({
            page: 1,
            page_size: 15,
            search: '',
            filters: { active: ['true'] },
            ordering: '-created_at',
          }),
      }),
  },
  {
    matches: (path) => path === '/user/payroll/people',
    prefetch: (queryClient) =>
      queryClient.prefetchQuery({
        queryKey: ['payroll-people', 1, '', '{}'],
        staleTime: 60 * 1000,
        queryFn: () =>
          fetchEmployeeList({
            search: '',
            page: 1,
            pageSize: 15,
            filters: { active: ['true'] },
            expand: 'teams.id,teams.name,managed_teams,current_position',
            fields:
              'id,first_name,last_name,full_name,profile_picture,contract_type,current_position.id,current_position.title,teams.id,teams.name,managed_teams.id,managed_teams.name,contract_type,country_of_work,salary,fte,total_benefits',
          }),
      }),
  },
  {
    matches: (path) => path.startsWith('/user/admin/document/') || path.startsWith('/user/document/'),
    prefetch: (queryClient, path) => {
      const routeType = path.split('/').pop() || 'documents'
      const docType = routeType === 'hrinfo' ? 'hr' : routeType
      const recipient = path.startsWith('/user/document/') ? null : undefined
      return queryClient.prefetchQuery({
        queryKey: ['documents', 1, '', docType, '{}', recipient],
        staleTime: 30 * 1000,
        queryFn: () =>
          getDocuments({
            search: '',
            page: 1,
            page_size: 15,
            doc_type: docType,
            filters: {},
            recipient: recipient ?? undefined,
            fields:
              'id,title,recipient.id,recipient.first_name,recipient.last_name,recipient.profile_picture,author.first_name,author.last_name,author.profile_picture,status,version,country,created_at,updated_at,file,signed_file,type',
          }),
      })
    },
  },
]

export const prefetchPathIntent = (path: string, queryClient?: QueryClient) => {
  if (!path || path.startsWith('mailto:') || warmedPaths.has(path)) return

  const preloader = routePreloaders.find((candidate) => candidate.matches(path))
  if (!preloader) return

  warmedPaths.add(path)
  void preloader.load().catch(() => {
    warmedPaths.delete(path)
  })

  if (!queryClient || warmedPathData.has(path)) return

  const tabDataPrefetcher = tabDataPrefetchers.find((candidate) => candidate.matches(path))
  if (!tabDataPrefetcher) return

  warmedPathData.add(path)
  void tabDataPrefetcher.prefetch(queryClient, path).catch(() => {
    warmedPathData.delete(path)
  })
}

const getCurrentWeekRange = () => {
  const today = new Date()
  const start = new Date(today)
  start.setDate(today.getDate() - ((today.getDay() + 6) % 7))
  const end = new Date(start)
  end.setDate(start.getDate() + 6)

  const format = (date: Date) => date.toISOString().slice(0, 10)

  return {
    date_from: format(start),
    date_to: format(end),
  }
}

export const prefetchEmployeeRowIntent = (queryClient: QueryClient, employeeId?: string | number | null) => {
  if (!employeeId) return
  const key = `employee:${employeeId}`
  if (warmedRows.has(key)) return
  warmedRows.add(key)

  void queryClient
    .prefetchQuery({
      queryKey: ['employee', String(employeeId)],
      queryFn: () => fetchEmployee(String(employeeId)),
      staleTime: 60 * 1000,
    })
    .catch(() => warmedRows.delete(key))
}

export const prefetchTeamRowIntent = (queryClient: QueryClient, teamId?: string | number | null) => {
  if (!teamId) return
  const key = `team:${teamId}`
  if (warmedRows.has(key)) return
  warmedRows.add(key)

  void queryClient
    .prefetchQuery({
      queryKey: ['team', String(teamId)],
      queryFn: () => fetchTeam(String(teamId)),
      staleTime: 60 * 1000,
    })
    .catch(() => warmedRows.delete(key))
}

export const prefetchContentRowIntent = (queryClient: QueryClient, contentId?: string | number | null) => {
  if (!contentId) return
  const key = `content:${contentId}`
  if (warmedRows.has(key)) return
  warmedRows.add(key)

  void queryClient
    .prefetchQuery({
      queryKey: ['content', String(contentId)],
      queryFn: () => fetchContent(String(contentId)),
      staleTime: 60 * 1000,
    })
    .catch(() => warmedRows.delete(key))
}

export const prefetchCandidateRowIntent = (queryClient: QueryClient, candidateId?: string | number | null) => {
  if (!candidateId) return
  const key = `candidate:${candidateId}`
  if (warmedRows.has(key)) return
  warmedRows.add(key)

  void queryClient
    .prefetchQuery({
      queryKey: ['candidate', String(candidateId)],
      queryFn: () => fetchCandidate(String(candidateId)),
      staleTime: 60 * 1000,
    })
    .catch(() => warmedRows.delete(key))
}

export const prefetchVacancyRowIntent = (queryClient: QueryClient, vacancyId?: string | number | null) => {
  if (!vacancyId) return
  const key = `vacancy:${vacancyId}`
  if (warmedRows.has(key)) return
  warmedRows.add(key)

  void queryClient
    .prefetchQuery({
      queryKey: ['vacancy', String(vacancyId)],
      queryFn: () => fetchVacancy(String(vacancyId)),
      staleTime: 60 * 1000,
    })
    .catch(() => warmedRows.delete(key))
}

export const prefetchProjectRowIntent = (queryClient: QueryClient, projectId?: string | number | null) => {
  if (!projectId) return
  const key = `project:${projectId}`
  if (warmedRows.has(key)) return
  warmedRows.add(key)

  const { date_from, date_to } = getCurrentWeekRange()
  const rangeKey = { projectId: String(projectId), date_from, date_to }

  void Promise.all([
    queryClient.prefetchQuery({
      queryKey: ['project-detail-snapshot', String(projectId), date_from, date_to],
      queryFn: async () => {
        const snapshot = await getProjectDetailSnapshot(String(projectId), { date_from, date_to })
        queryClient.setQueryData(['projects', String(projectId)], snapshot.project)
        queryClient.setQueryData(['projects', String(projectId), 'summary'], snapshot.summary)
        queryClient.setQueryData(['time-entries', rangeKey], snapshot.time_entries)
        return snapshot
      },
      staleTime: 60 * 1000,
    }),
    queryClient.prefetchQuery({
      queryKey: ['projects', String(projectId)],
      queryFn: () => getProjectById(String(projectId)),
      staleTime: 60 * 1000,
    }),
    queryClient.prefetchQuery({
      queryKey: ['projects', String(projectId), 'summary'],
      queryFn: () => getProjectSummary(String(projectId)),
      staleTime: 60 * 1000,
    }),
    queryClient.prefetchQuery({
      queryKey: ['time-entries', rangeKey],
      queryFn: () => getTimeEntries({ project: String(projectId), date_from, date_to }),
      staleTime: 60 * 1000,
    }),
  ]).catch(() => {
    warmedRows.delete(key)
  })
}
